import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Play, ChevronRight, Copy, Loader2, Check, X,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_LABELS = {
  la_course: 'La Course', la_tour: 'La Tour', robot_balayeur: 'Robot Balayeur',
  tri_express: 'Tri Express', batisseur: 'Batisseur', detective: 'Le Detective',
  memoire: 'La Memoire', avalanche: "L'Avalanche", le_chef: 'Le Chef',
  carte_du_canada: 'Carte du Canada', duel_conjugaison: 'Le Duel',
  horloge_folle: "L'Horloge Folle",
};

const MODE_LABELS = { school: 'School Year', level: 'By Level', topic: 'By Topic' };
const LEVEL_LABELS = { 'pre-A1': 'Beginner', 'A1': 'Elementary', 'A1-A2': 'Intermediate', 'A2': 'Upper Intermediate' };

export function SamplePlaylistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);

  // Copy flow states: 'idle' -> 'expanded' -> 'naming' -> 'copying'
  const [copyState, setCopyState] = useState('idle');
  const [copyName, setCopyName] = useState('');

  const fetchTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/templates/${id}`);
      setTemplate(res.data);
    } catch {
      navigate('/dashboard');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTemplate().finally(() => setLoading(false));
  }, [fetchTemplate]);

  const launchSession = (session) => {
    const config = session.config || {};
    const params = new URLSearchParams(config);
    navigate(`/play/${session.game}/setup?${params}`);
  };

  const handleCopyClick = () => {
    setCopyState('expanded');
  };

  const handleProceedToName = () => {
    setCopyName(template?.title || '');
    setCopyState('naming');
  };

  const handleCancelCopy = () => {
    setCopyState('idle');
    setCopyName('');
  };

  const handleConfirmCopy = async () => {
    setCopyState('copying');
    try {
      const res = await axios.post(`${API_URL}/teacher/copy-sample/${id}`, {
        name: copyName.trim() || template?.title,
      });
      navigate(`/playlists/${res.data.playlist_id}`);
    } catch {
      setCopyState('expanded');
    }
  };

  if (loading) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8' }}>Loading...</div>
    </div>
  );
  if (!template) return null;

  const allSessions = [];
  for (const phase of (template.phases || [])) {
    for (const session of (phase.sessions || [])) {
      allSessions.push(session);
    }
  }

  return (
    <div data-testid="sample-playlist-detail-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      {/* Header row with back + title + Copy Playlist button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <button
          data-testid="sample-playlist-back-btn"
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#64748B',
            padding: '0.25rem', display: 'flex', alignItems: 'center',
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: '#1A1D20', margin: 0, flex: 1 }}>
          {template.title}
        </h1>

        {/* Copy Playlist trigger button — top right */}
        {copyState === 'idle' && (
          <button
            data-testid="copy-playlist-trigger-btn"
            onClick={handleCopyClick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.5rem 1rem', borderRadius: '0.5rem',
              background: 'white', color: '#1E2140', border: '1.5px solid #E2E8F0',
              fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#1E2140'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,33,64,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <Copy size={14} /> Copy Playlist
          </button>
        )}
      </div>

      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#94A3B8', margin: '0 0 1rem 2.5rem' }}>
        {allSessions.length} session{allSessions.length !== 1 ? 's' : ''} in sequence &middot; Sample playlist
      </p>

      {/* Description */}
      {template.description && (
        <div
          data-testid="sample-playlist-description"
          style={{
            margin: '0 0 1.5rem 2.5rem', maxWidth: 600,
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#475569',
            lineHeight: 1.6,
          }}
        >
          {template.description}
        </div>
      )}

      {/* Copy expanded panel — shown after clicking "Copy Playlist" */}
      {(copyState === 'expanded' || copyState === 'naming' || copyState === 'copying') && (
        <div
          data-testid="copy-playlist-panel"
          style={{
            margin: '0 0 1.5rem 2.5rem', maxWidth: 520,
            padding: '1rem 1.25rem', borderRadius: '0.75rem',
            background: 'white', border: '1.5px solid #E2E8F0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#475569',
            lineHeight: 1.6, margin: '0 0 0.875rem 0',
          }}>
            Want to use this as a starting point? Copy it to your playlists so you can edit, reorder, and add your own lessons from the rest of the{' '}
            <span style={{ textDecoration: 'underline', fontWeight: 600 }}>library</span>.
          </p>

          {/* Step 2: naming input */}
          {(copyState === 'naming' || copyState === 'copying') ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <label style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', fontWeight: 600, color: '#64748B' }}>
                Playlist name
              </label>
              <input
                data-testid="copy-playlist-name-input"
                value={copyName}
                onChange={e => setCopyName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConfirmCopy()}
                autoFocus
                style={{
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600,
                  color: '#1A1D20', border: '1px solid #E2E8F0', borderRadius: '0.5rem',
                  padding: '0.5rem 0.75rem', outline: 'none', background: '#F8F9FB',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => e.currentTarget.style.borderColor = '#4C6EF5'}
                onBlur={e => e.currentTarget.style.borderColor = '#E2E8F0'}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  data-testid="copy-confirm-btn"
                  onClick={handleConfirmCopy}
                  disabled={copyState === 'copying'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                    background: copyState === 'copying' ? '#94A3B8' : '#1E2140',
                    color: 'white', border: 'none',
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                    cursor: copyState === 'copying' ? 'default' : 'pointer',
                  }}
                >
                  {copyState === 'copying'
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Copying...</>
                    : <><Check size={14} /> Copy</>
                  }
                </button>
                <button
                  data-testid="copy-cancel-btn"
                  onClick={handleCancelCopy}
                  disabled={copyState === 'copying'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.5rem 1rem', borderRadius: '0.5rem',
                    background: 'transparent', color: '#64748B',
                    border: '1px solid #E2E8F0',
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Step 1: just the copy action button */
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                data-testid="copy-to-playlists-btn"
                onClick={handleProceedToName}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                  background: '#1E2140', color: 'white', border: 'none',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                  cursor: 'pointer', transition: 'transform 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Copy size={14} /> Copy to My Playlists
              </button>
              <button
                data-testid="copy-dismiss-btn"
                onClick={handleCancelCopy}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0.5rem', borderRadius: '0.5rem',
                  background: 'transparent', color: '#94A3B8',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sessions list */}
      {allSessions.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '3rem 2rem', background: 'white',
          borderRadius: '1rem', border: '1px solid #E2E8F0',
        }}>
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', fontSize: '0.925rem' }}>
            No sessions in this playlist.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {allSessions.map((session, index) => {
            const isExpanded = expandedItem === index;
            const gameName = GAME_LABELS[session.game] || session.game;
            const config = session.config || {};

            return (
              <div
                key={index}
                data-testid={`sample-item-${index}`}
                style={{
                  background: 'white', borderRadius: '0.875rem',
                  border: '1px solid #E2E8F0', overflow: 'hidden',
                }}
              >
                {/* Item row */}
                <div
                  onClick={() => setExpandedItem(isExpanded ? null : index)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.875rem 1.25rem', cursor: 'pointer',
                    background: isExpanded ? '#F8FAFF' : 'white',
                    transition: 'background 0.1s',
                  }}
                >
                  {/* Day number badge */}
                  <div style={{
                    width: 36, height: 36, borderRadius: '0.5rem',
                    background: '#F0F4FF', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0,
                    fontFamily: 'Fredoka, sans-serif', fontSize: '0.9rem',
                    fontWeight: 600, color: '#4C6EF5',
                  }}>
                    {index + 1}
                  </div>

                  {/* Label + topic */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.9rem' }}>
                      {session.label || `Day ${index + 1}`}
                    </span>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.1rem' }}>
                      {session.topic} &middot; {gameName}
                    </div>
                  </div>

                  {/* Game badge */}
                  <span style={{
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600,
                    color: '#4C6EF5', background: '#F0F4FF', padding: '0.2rem 0.6rem',
                    borderRadius: '1rem', whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    {gameName}
                  </span>

                  {/* Time chip */}
                  <span style={{
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#94A3B8',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    ~{session.play_minutes}m
                  </span>

                  <ChevronRight
                    size={14} color="#CBD5E1"
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}
                  />
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{
                    padding: '0.75rem 1.25rem 1rem', borderTop: '1px solid #F1F5F9',
                    background: '#FAFBFC',
                  }}>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
                      {config.mode && <DetailChip label="Mode" value={MODE_LABELS[config.mode] || config.mode} />}
                      {config.level && <DetailChip label="Level" value={LEVEL_LABELS[config.level] || config.level} />}
                      {config.difficulty && <DetailChip label="Difficulty" value={config.difficulty} />}
                      {config.topic && <DetailChip label="Topic" value={session.topic} />}
                      {session.item_count && <DetailChip label="Items" value={`${session.item_count}`} />}
                    </div>
                    <button
                      data-testid={`sample-launch-${index}`}
                      onClick={(e) => { e.stopPropagation(); launchSession(session); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                        background: '#1E2140', color: 'white', border: 'none',
                        fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Play size={14} fill="white" /> Launch Game
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetailChip({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#1A1D20' }}>
        {value}
      </div>
    </div>
  );
}
